import React, { Fragment, useMemo } from 'react';
import PageContext from '../../contexts/pageContext';
import getTheme from '../../theme';
import { ThemeProvider } from '../../theme/theming';
import FontSeeder from '../fontSeeder';
import SectionRenderer from './sectionRenderer';
import Header from '../../components/header';
import Footer from '../../components/footer';
import defaultMetadata, {
  defaultLayout,
  defaultTheme
} from '../defaultMetadata';
import { generateTheme } from '../../theme/themeV2';

export default function PageRenderer({
  pageData: {
    metadata: {
      theme: {
        color = defaultTheme.color,
        font = defaultTheme.font
      } = defaultTheme,
      layout = defaultLayout
    } = defaultMetadata,
    sections,
    header,
    footer,
    validations = {},
    _id,
    isLandingPage = false,
    isCustomWebsite = false
  },
  isMobile = false,
  isLandingPages = false,
  baseURLs = {},
  hashToken,
  SectionWrapper,
  isMasterTemplate,
  basePath,
  countryCode,
  currencySymbol,
  isPreview,
  isEdit,
  sectionPlaceholder,
  templateId,
  isTutorWebsite = false,
  extraProps,
  hideLogin
}) {
  const navList = header?.navs;
  const context = useMemo(
    () => ({
      isMobile,
      isLandingPages,
      layout,
      baseURLs,
      hashToken,
      isMasterTemplate,
      basePath,
      isPreview,
      isEdit,
      templateId,
      navList,
      validations,
      isTutorWebsite,
      extraProps,
      hideLogin,
      isCustomWebsite,
      _id,
      countryCode,
      currencySymbol
    }),
    [
      isMobile,
      isLandingPages,
      layout,
      baseURLs,
      hashToken,
      isPreview,
      isEdit,
      templateId,
      navList,
      isMasterTemplate,
      basePath,
      validations,
      isTutorWebsite,
      extraProps,
      hideLogin,
      _id,
      countryCode,
      currencySymbol
    ]
  );
  const theme = useMemo(
    () => generateTheme(color, font, context.isMobile),
    [color, font, context.isMobile]
  );
  const Wrapper = SectionWrapper || Fragment;

  return (
    <ThemeProvider theme={theme}>
      <PageContext.Provider value={context}>
        <FontSeeder fontFamily={font} />
        {!!header && (
          <Wrapper
            {...(!!SectionWrapper && { sectionData: header, isHeader: true })}
          >
            <Header
              data={header}
              isLandingPage={isLandingPage}
              extraProps={extraProps}
            />
          </Wrapper>
        )}
        {sections?.length
          ? sections?.map((sectionData, sectionIndex) => (
              <Wrapper
                {...(!!SectionWrapper && { sectionData, sectionIndex })}
                key={sectionData.type + (sectionData._id || sectionIndex)}
              >
                <SectionRenderer
                  sectionData={sectionData}
                  extraProps={extraProps}
                  isCustomWebsite={isCustomWebsite}
                  sectionIndex={sectionIndex}
                />
              </Wrapper>
            ))
          : sectionPlaceholder}
        {!!footer && (
          <Wrapper
            {...(!!SectionWrapper && { sectionData: footer, isFooter: true })}
          >
            <Footer data={footer} />
          </Wrapper>
        )}
      </PageContext.Provider>
    </ThemeProvider>
  );
}
